/**
 * Fades sections up as they scroll into view.
 *
 * Three deliberate constraints:
 *
 * 1. The opt-in class is added here, not in the markup. If this file fails to
 *    load or is blocked, every section renders visible rather than blank —
 *    which is what happens when the hidden state lives in CSS.
 * 2. The first section is skipped. It is the largest contentful paint on most
 *    templates, and fading it in delays the metric for no design benefit.
 * 3. Both classes are removed once the transition ends. An element with a
 *    `transform` is a containing block for its `position: fixed` descendants,
 *    so leaving one behind would quietly break any drawer or sticky bar added
 *    to that section later.
 */
(function () {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const DURATION = 700;

  function start() {
    const main = document.querySelector('main');
    if (!main) return;

    // Header and footer groups sit outside main and are deliberately untouched.
    const sections = Array.from(main.querySelectorAll(':scope > .shopify-section')).slice(1);
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          observer.unobserve(el);

          requestAnimationFrame(() => {
            el.classList.add('is-revealed');
            setTimeout(() => el.classList.remove('js-reveal', 'is-revealed'), DURATION);
          });
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    sections.forEach((section) => {
      // Anything already on screen at load skips the animation entirely.
      const box = section.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.9) return;

      /**
       * Number the children of any opted-in grid so CSS can stagger them off a
       * single custom property. Capped at eight: past that the last card waits
       * long enough for the delay to read as lag rather than choreography.
       */
      section.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
        Array.from(group.children).forEach((child, i) => {
          child.style.setProperty('--reveal-i', String(Math.min(i, 8)));
        });
      });

      section.classList.add('js-reveal');
      observer.observe(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
